import { useQueryClient } from "react-query";
import { Banner, SectionBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { MODIFY, PRODUCT_ACTIVITY_DATA, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { useUpsertProductOrganizationActivityData } from "src/api/client-api/productOrganizationActivityData/useUpsertProductOrganizationActivityData";
import { ActivitiesHeader } from "./ActivitiesData/ActivitiesHeader";
import { OrganizationsList } from "./ActivitiesData/OrganizationsList";
import { getOneProductKey } from "src/api/client-api/products/useGetOneProduct";
import { ActivityDataStatistics } from "src/components/ActivityDataGrid/ActivityDataStatistics";
import { productGridKey } from "src/api/client-api/products/useGetAllproduct";
import { getProductScoresKey } from "src/api/client-api/olapScores/useGetProductScores";
import {
    buildActivityDataWrites,
    getOwnActivitiesFormData,
    getTotalQuantity,
    OrganizationActivitiesFormData,
} from "./ActivitiesData/activitiesSpace";

export const ProductActivitiesData = () => {
    const [toggleEditMode, setToggleEditMode] = useState(false);
    const { product } = useProductMetadataContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const { canUser, currentCampaign, selectedCompany } = useFoodpilotContext();

    const [prevActivities, setPrevActivities] = useState<OrganizationActivitiesFormData>({});
    const [activities, setActivities] = useState<OrganizationActivitiesFormData>({});
    const [areActivitiesSaving, setActivitiesSaving] = useState<boolean>(false);

    useEffect(() => {
        const activitiesData = getOwnActivitiesFormData(product.shares ?? [], selectedCompany?.id, currentCampaign?.id);

        setPrevActivities({ ...activitiesData });
        setActivities(activitiesData);
    }, [product, currentCampaign, selectedCompany?.id]);

    const updateActivityData = useUpsertProductOrganizationActivityData({ productId: product.id });

    if (product === undefined || currentCampaign === null || selectedCompany === null) return;

    const canModifyActivityData = canUser(MODIFY, PRODUCT_ACTIVITY_DATA);

    const totalQuantity = getTotalQuantity(activities);
    const isProductOwner = selectedCompany.id === product.ownedBy?.id;

    return (
        <Stack padding={theme.spacing(2, 0)} gap={theme.spacing(2)}>
            <Typography variant="h2">
                {t("product.activity_data", "Product activity data")} - {currentCampaign.label}
            </Typography>
            <SectionBox
                header={
                    <ActivitiesHeader
                        product={product}
                        toggleEditMode={toggleEditMode}
                        onActionClick={async () => {
                            if (!canModifyActivityData) return;

                            const newValue = !toggleEditMode;

                            if (!newValue) {
                                setActivitiesSaving(true);

                                const writes = buildActivityDataWrites(activities, prevActivities, currentCampaign.id);

                                try {
                                    await Promise.all(writes.map((write) => updateActivityData.mutateAsync(write)));

                                    setActivitiesSaving(false);
                                    setToggleEditMode(false);
                                    setPrevActivities(activities);

                                    queryClient.invalidateQueries([productGridKey]);
                                    queryClient.invalidateQueries([getOneProductKey, product.id.toString()]);
                                    queryClient.invalidateQueries([getProductScoresKey, product.id]);
                                } catch (error) {
                                    setActivitiesSaving(false);
                                    console.error("Failed to save activity data:", error);
                                }

                                return;
                            }

                            setToggleEditMode(true);
                        }}
                        areActivitiesSaving={areActivitiesSaving}
                    />
                }
            >
                <Box sx={{ display: "flex", gap: "2rem", flexDirection: "column" }}>
                    <ActivityDataStatistics
                        statistics={[
                            {
                                title: t("product.activity_data.total_quantity", "Total quantity"),
                                value: totalQuantity,
                                unit: "T",
                            },
                        ]}
                    />

                    {isProductOwner && (
                        <Banner variant="info">
                            {t(
                                "product.activity_data.owner_notice",
                                "Each associated structure keeps its own activity data. Select a structure to view and edit its data.",
                            )}
                        </Banner>
                    )}

                    <OrganizationsList
                        activities={activities}
                        onActivitiesUpdate={setActivities}
                        toggleEditMode={toggleEditMode}
                    />
                </Box>
            </SectionBox>
        </Stack>
    );
};
